/**
 * Справочник контрагентов: полнота реквизитов для платёжки — одно правило для фильтра
 * «Требуют уточнения», отметки в строке и показателя «С полными реквизитами»; источники
 * карточек; проверка формы карточки и данные для сохранения.
 *
 * Проверки формы повторяют серверные (`routes/financier/counterparties.rs`); решает всё
 * равно сервер, его текст показываем как есть. Модуль без импортов значений —
 * проверяется node:test (`model.test.mjs`).
 */
import type {
  FinCounterpartyRow,
  UpdateCounterpartyInput,
  UpsertCounterpartyInput,
} from '@shared/api';
import type { CounterpartyKpiMetrics } from './types';

export type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

// ── Полнота реквизитов ───────────────────────────────────────────────────

type Requisites = Pick<FinCounterpartyRow, 'inn' | 'account_number' | 'bik'>;

/** Счёт-заглушка: по нему выгрузка в 1С платёжку не формирует. */
const DUMMY_ACCOUNT = '40702810000000000000';

/**
 * Чего не хватает для платёжного поручения — те же условия, что у выгрузки в 1С: ИНН из
 * 10 или 12 цифр, расчётный счёт из 20 цифр, БИК из 9 цифр. Банк и корр. счёт банк-клиент
 * дополняет по БИК. Пустой список — реквизиты полные.
 */
export function requisitesProblems(cp: Requisites): string[] {
  const problems: string[] = [];
  const inn = (cp.inn ?? '').trim();
  if (!/^(\d{10}|\d{12})$/.test(inn) || /^0+$/.test(inn)) {
    problems.push('ИНН не из 10 или 12 цифр');
  }
  const account = (cp.account_number ?? '').trim();
  if (!account) {
    problems.push('нет расчётного счёта');
  } else if (!/^\d{20}$/.test(account) || account === DUMMY_ACCOUNT) {
    problems.push('счёт не из 20 цифр');
  }
  const bik = (cp.bik ?? '').trim();
  if (!bik) {
    problems.push('нет БИК');
  } else if (!/^\d{9}$/.test(bik)) {
    problems.push('БИК не из 9 цифр');
  }
  return problems;
}

export function requisitesComplete(cp: Requisites): boolean {
  return requisitesProblems(cp).length === 0;
}

/** Фильтр «Реквизиты»: все, полные, требуют уточнения. */
export type RequisitesFilter = 'all' | 'complete' | 'incomplete';

export function matchesRequisites(cp: Requisites, filter: RequisitesFilter): boolean {
  if (filter === 'complete') return requisitesComplete(cp);
  if (filter === 'incomplete') return !requisitesComplete(cp);
  return true;
}

// ── Источники карточек ───────────────────────────────────────────────────

/** Откуда пришла карточка — коды, которые пишет сервер, в порядке фильтра. */
export const SOURCES: ReadonlyArray<{ code: string; label: string; tone: Tone }> = [
  { code: 'manual', label: 'Внесён вручную', tone: 'neutral' },
  { code: 'contracts_buffer', label: 'Из договоров Юриста', tone: 'neutral' },
  { code: '1c_api', label: '1С: синхронизация', tone: 'info' },
  { code: '1c_webhook', label: '1С: передан из 1С', tone: 'info' },
  { code: '1c_import', label: 'Файл 1С: Клиент-банк', tone: 'info' },
  { code: 'json_import', label: 'Пакет JSON из 1С', tone: 'info' },
  { code: 'tabular_import', label: 'Таблица Excel', tone: 'warning' },
  { code: 'bank_statement', label: 'Выписка банка', tone: 'warning' },
];

/** Коды, которые допускает база, но нынешний код не пишет: старые карточки. */
const OTHER_SOURCES: Readonly<Record<string, string>> = {
  '1c_statement': '1С: выписка',
  '1c_document': '1С: документ',
  excel: 'Excel',
  contracts: 'Договоры',
  api_import: 'Внешняя система',
};

export function sourceLabel(code: string): string {
  return SOURCES.find((s) => s.code === code)?.label ?? OTHER_SOURCES[code] ?? code;
}

export function sourceTone(code: string): Tone {
  return SOURCES.find((s) => s.code === code)?.tone ?? 'neutral';
}

/** Пункты фильтра «Источник»: известные коды и любые другие, что есть в справочнике. */
export function sourceOptions(seen: Iterable<string>): Array<{ code: string; label: string }> {
  const options = SOURCES.map(({ code, label }) => ({ code, label }));
  const known = new Set(options.map((o) => o.code));
  for (const code of seen) {
    if (code && !known.has(code)) {
      known.add(code);
      options.push({ code, label: sourceLabel(code) });
    }
  }
  return options;
}

/** Карточка пришла из 1С: синхронизация, передача из 1С, файлы 1С. */
export function isFrom1C(source: string): boolean {
  return source.startsWith('1c') || source === 'json_import';
}

// ── Показатели и полнота выдачи ──────────────────────────────────────────

type KpiRow = Requisites & Pick<FinCounterpartyRow, 'source' | 'counterparty_type'>;

/**
 * Показатели справочника. «Всего» — число с сервера; остальные — по загруженным
 * карточкам (`countedCount`): если загружены не все, экран так и говорит.
 */
export function computeKpis(rows: readonly KpiRow[], total: number): CounterpartyKpiMetrics {
  return {
    totalCount: Math.max(total, rows.length),
    countedCount: rows.length,
    withBankDetailsCount: rows.filter(requisitesComplete).length,
    synced1CCount: rows.filter((c) => isFrom1C(c.source)).length,
    activeSuppliersCount: rows.filter(
      (c) => c.counterparty_type === 'supplier' || c.counterparty_type === 'subcontractor',
    ).length,
  };
}

/** «Показаны первые N из M» — сервер отдал не все подходящие карточки. */
export function shownCaption(shown: number, total: number): string | null {
  if (total <= shown) return null;
  const n = (v: number) => v.toLocaleString('ru-RU');
  return `Показаны первые ${n(shown)} из ${n(total)}. Уточните поиск по названию или ИНН.`;
}

// ── Форма карточки ───────────────────────────────────────────────────────

export interface CounterpartyForm {
  name: string;
  fullName: string;
  inn: string;
  kpp: string;
  ogrn: string;
  type: string;
  account: string;
  bik: string;
  bankName: string;
  corrAccount: string;
  category: string;
  notes: string;
}

export type CounterpartyFormField = keyof CounterpartyForm;

export const EMPTY_FORM: CounterpartyForm = {
  name: '',
  fullName: '',
  inn: '',
  kpp: '',
  ogrn: '',
  type: 'supplier',
  account: '',
  bik: '',
  bankName: '',
  corrAccount: '',
  category: 'materials',
  notes: '',
};

export function formFromCard(cp: FinCounterpartyRow): CounterpartyForm {
  return {
    name: cp.name,
    fullName: cp.full_name ?? '',
    inn: cp.inn,
    kpp: cp.kpp ?? '',
    ogrn: cp.ogrn ?? '',
    type: cp.counterparty_type,
    account: cp.account_number ?? '',
    bik: cp.bik ?? '',
    bankName: cp.bank_name ?? '',
    corrAccount: cp.corr_account ?? '',
    category: cp.default_category || 'materials',
    notes: cp.notes ?? '',
  };
}

export interface CounterpartyFormProblem {
  field: CounterpartyFormField;
  message: string;
}

const onlyDigits = (s: string, lens: number[]) => lens.includes(s.length) && /^\d+$/.test(s);

/**
 * Первая ошибка формы или `null`. В правке ИНН и КПП не проверяются: они не меняются.
 * Пустые реквизиты — не ошибка: карточку дополнят позже, а фильтр покажет её в
 * «Требуют уточнения».
 */
export function checkForm(
  form: CounterpartyForm,
  mode: 'create' | 'edit',
): CounterpartyFormProblem | null {
  if (!form.name.trim()) {
    return { field: 'name', message: 'Укажите рабочее наименование контрагента' };
  }
  if (mode === 'create') {
    if (!onlyDigits(form.inn.trim(), [10, 12])) {
      return { field: 'inn', message: 'ИНН — 10 цифр у организации или 12 у ИП' };
    }
    const kpp = form.kpp.trim();
    if (kpp && !onlyDigits(kpp, [9])) {
      return { field: 'kpp', message: 'КПП — ровно 9 цифр' };
    }
  }
  const ogrn = form.ogrn.trim();
  if (ogrn && !onlyDigits(ogrn, [13, 15])) {
    return { field: 'ogrn', message: 'ОГРН — 13 цифр у организации или 15 у ИП' };
  }
  const account = form.account.trim();
  if (account && !onlyDigits(account, [20])) {
    return { field: 'account', message: 'Расчётный счёт — ровно 20 цифр' };
  }
  const bik = form.bik.trim();
  if (bik && !onlyDigits(bik, [9])) {
    return { field: 'bik', message: 'БИК банка — ровно 9 цифр' };
  }
  const corr = form.corrAccount.trim();
  if (corr && !onlyDigits(corr, [20])) {
    return { field: 'corrAccount', message: 'Корр. счёт банка — ровно 20 цифр' };
  }
  return null;
}

/** Новая карточка: пустые поля не передаются. */
export function createPayload(form: CounterpartyForm): UpsertCounterpartyInput {
  const opt = (v: string) => v.trim() || undefined;
  return {
    name: form.name.trim(),
    full_name: opt(form.fullName),
    inn: form.inn.trim(),
    kpp: opt(form.kpp),
    ogrn: opt(form.ogrn),
    counterparty_type: form.type,
    account_number: opt(form.account),
    bik: opt(form.bik),
    bank_name: opt(form.bankName),
    corr_account: opt(form.corrAccount),
    default_category: form.category,
    notes: opt(form.notes),
    source: 'manual',
  };
}

/** Правка карточки: передаются все поля формы, пустая строка — очистка поля. */
export function updatePayload(form: CounterpartyForm): UpdateCounterpartyInput {
  return {
    name: form.name.trim(),
    full_name: form.fullName.trim(),
    ogrn: form.ogrn.trim(),
    counterparty_type: form.type,
    account_number: form.account.trim(),
    bik: form.bik.trim(),
    bank_name: form.bankName.trim(),
    corr_account: form.corrAccount.trim(),
    default_category: form.category,
    notes: form.notes.trim(),
  };
}
